import { useState } from "react";
import { LayoutGrid, List } from "lucide-react";
import { cn } from "@/lib/utils";

const STORAGE_PREFIX = "view_mode:";

/** Remembers list-vs-cards per page, per browser. */
export function useViewMode(key, defaultMode = "list") {
  const [mode, setModeState] = useState(() => {
    // Wide tables don't suit a phone, so cards are the default there.
    const fallback = defaultMode === "list" && window.innerWidth < 768 ? "grid" : defaultMode;
    try {
      return window.localStorage.getItem(STORAGE_PREFIX + key) || fallback;
    } catch {
      return fallback;
    }
  });
  const setMode = (next) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_PREFIX + key, next);
    } catch {
      /* private browsing */
    }
  };
  return [mode, setMode];
}

/**
 * The List / Cards switch used on every page that offers both layouts.
 * `options` lets a page offer a third layout (Production has a Board).
 */
export default function ViewToggle({ mode, onChange, options }) {
  const opts = options || [
    { value: "list", label: "List", icon: List },
    { value: "grid", label: "Cards", icon: LayoutGrid },
  ];
  return (
    <div role="group" aria-label="Layout" className="inline-flex h-9 rounded-lg border border-line overflow-hidden shrink-0 bg-card">
      {opts.map((o, i) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={mode === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            "flex items-center gap-1.5 px-3 text-[12.5px] font-medium transition-colors",
            i > 0 && "border-l border-line",
            mode === o.value ? "bg-brand-light text-brand" : "text-muted2 hover:text-ink"
          )}
        >
          <o.icon className="w-3.5 h-3.5" /> {o.label}
        </button>
      ))}
    </div>
  );
}
